import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { Button } from '@src/components/ui';
import { updateClinic } from '../../../api/ClinicApi';
import { fetchSalesReps } from '../../../api/SalesRepAdminApi';
const toOption = (item) => ({
    id: item.id,
    sales_rep_id: item.salesRepId ?? item.sales_rep_id,
    name: item.name,
});
const AssignSalesRepPanel = ({ clinicId, clinicName, currentSalesRep, onSaved }) => {
    const [options, setOptions] = useState([]);
    const [optionsLoading, setOptionsLoading] = useState(true);
    const [optionsError, setOptionsError] = useState('');
    const [selectedId, setSelectedId] = useState(currentSalesRep?.id ?? '');
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const response = await fetchSalesReps({ is_active: true });
                if (cancelled)
                    return;
                const body = response.body;
                const rows = Array.isArray(body) ? body : body?.results || [];
                setOptions(rows.map(toOption));
            }
            catch (err) {
                if (cancelled)
                    return;
                console.error('Error loading sales reps:', err);
                setOptionsError('Failed to load sales reps');
            }
            finally {
                if (!cancelled)
                    setOptionsLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);
    // A deactivated rep stays visible (and selected) so saving never silently drops it.
    const selectableOptions = currentSalesRep && !options.some((option) => option.id === currentSalesRep.id)
        ? [currentSalesRep, ...options]
        : options;
    const unchanged = selectedId === (currentSalesRep?.id ?? '');
    const handleSave = async () => {
        setSaving(true);
        setSaveError('');
        try {
            await updateClinic(clinicId, { sales_rep: selectedId || null });
            onSaved(selectableOptions.find((option) => option.id === selectedId) ?? null);
        }
        catch (err) {
            console.error('Error assigning sales rep:', err);
            const body = err?.response?.body;
            const message = body?.salesRep?.[0] ?? body?.detail;
            setSaveError(typeof message === 'string' && message ? message : 'Failed to assign sales rep');
        }
        finally {
            setSaving(false);
        }
    };
    return (_jsxs("div", { children: [_jsxs("p", { style: { marginBottom: '16px', color: '#555' }, children: ["Choose the sales rep responsible for ", _jsx("strong", { children: clinicName }), ". New orders from this clinic are attributed to the rep assigned at the time they are placed; existing orders are not changed."] }), !!optionsError && _jsx("div", { role: "alert", style: { color: '#AC0824', marginBottom: '12px' }, children: optionsError }), !!saveError && _jsx("div", { role: "alert", style: { color: '#AC0824', marginBottom: '12px' }, children: saveError }), _jsx("label", { htmlFor: "assign-sales-rep", style: { display: 'block', marginBottom: '8px', fontSize: '14px' }, children: "Sales Rep" }), _jsxs("select", { id: "assign-sales-rep", value: selectedId, disabled: optionsLoading, onChange: (event) => setSelectedId(event.target.value), style: { width: '100%', padding: '8px', marginBottom: '24px', boxSizing: 'border-box' }, children: [_jsx("option", { value: "", children: "Unassigned" }), selectableOptions.map((option) => (_jsxs("option", { value: option.id, children: [option.sales_rep_id, " - ", option.name] }, option.id)))] }), _jsx(Button, { label: saving ? 'Saving...' : 'Save Sales Rep', variant: "primary", disabled: saving || optionsLoading || unchanged, onClick: handleSave })] }));
};
export default AssignSalesRepPanel;
